import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    CircularProgress,
    Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useSnackbars } from '@assembly/hooks'
import { SnackbarType } from '@assembly/contexts'
import { getContractByCountryAndVersion } from '@assembly/api/contracts'

interface ContractViewerModalProps {
    open: boolean
    onClose: () => void
    versionCode: string
    countryCode: string
}

export default function ContractViewerModal({
    open,
    onClose,
    versionCode,
    countryCode,
}: ContractViewerModalProps) {
    const { addAlert } = useSnackbars()
    const [contractHTML, setContractHTML] = useState<string>('')
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (open && versionCode && countryCode) {
            const fetchContract = async () => {
                try {
                    setLoading(true)
                    const response = await getContractByCountryAndVersion(countryCode, versionCode)
                    
                    if (response.data.status === 'SUCCESS') {
                        setContractHTML(response.data.message)
                    } else {
                        const errorMessage = response.data.message || 'Failed to fetch contract'
                        addAlert({ message: errorMessage, type: SnackbarType.Error })
                        setContractHTML('')
                    }
                } catch (error: any) {
                    const errorMessage =
                        error?.response?.data?.message || 'Failed to fetch contract'
                    addAlert({ message: errorMessage, type: SnackbarType.Error })
                    setContractHTML('')
                } finally {
                    setLoading(false)
                }
            }
            fetchContract()
        } else {
            setContractHTML('')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, versionCode, countryCode])

    const handleClose = () => {
        setContractHTML('')
        onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: {
                    height: '90vh',
                    maxHeight: '90vh',
                },
            }}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">
                        Contract - {versionCode} ({countryCode})
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="400px"
                    >
                        <CircularProgress />
                    </Box>
                ) : contractHTML ? (
                    <Box
                        sx={{
                            maxHeight: 'calc(90vh - 200px)',
                            overflow: 'auto',
                            '& .contract': {
                                fontFamily: 'Times New Roman, Times, serif',
                            },
                        }}
                        dangerouslySetInnerHTML={{ __html: contractHTML }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                        No contract content available
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}

